import { useEffect, useRef } from 'react';

import type { TipTopWidgetConfig } from '@/api/types';

const WIDGET_SCRIPT = 'https://widget.tiptoppay.kz/bundles/widget.js';

type TipTopWidgetInstance = {
  start: (params: TipTopWidgetConfig) => Promise<unknown>;
  oncomplete?: (result: unknown) => void;
};

declare global {
  interface Window {
    tiptop?: {
      Widget: new () => TipTopWidgetInstance;
    };
  }
}

function loadTipTopScript(): Promise<void> {
  if (window.tiptop?.Widget) {
    return Promise.resolve();
  }
  const existing = document.querySelector(`script[src="${WIDGET_SCRIPT}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('TipTop script failed')));
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = WIDGET_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('TipTop script failed'));
    document.head.appendChild(script);
  });
}

type Props = {
  config: TipTopWidgetConfig;
  onComplete?: (result: unknown) => void;
  onError?: (error: unknown) => void;
};

export function TipTopPayWidget({ config, onComplete, onError }: Props): null {
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    let cancelled = false;

    loadTipTopScript()
      .then(() => {
        if (cancelled || !window.tiptop?.Widget) {
          return;
        }
        const widget = new window.tiptop.Widget();
        widget.oncomplete = (result) => {
          onComplete?.(result);
        };
        return widget.start(config);
      })
      .then((result) => {
        if (!cancelled && result !== undefined) {
          onComplete?.(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          onError?.(err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [config, onComplete, onError]);

  return null;
}
